/**
 * Envelope register API (TMMAPP-0006 / TMMAPP-0011, design §8.5 `register`, §7.8
 * projections) — wire types from `src/api/generated/`; normalized into the module's domain type.
 * A line is the allocation in PUBLISHED terms — amount, category, date, payee, source-account
 * label — never the source transaction's full amount. Money is integer minor units in the
 * virtual account OWNER's currency. Pages are cursor-based: `{ items, page: { nextCursor? } }`.
 *
 * The virtual account ledger (TMMAPI-0145 item 9): one row per bank line with `publishedMinor`, its
 * `allocations[]` parts — an envelope's, or `{ unassigned: true }` — each with its category and
 * `contra` marker (MM-D-128), a running balance, server-side filters (`envelopeId`, `unassigned`,
 * `uncategorized`, `q`, AND) and `totals`. The Unassigned register (item 11) lists the Unassigned
 * parts in the envelope register's row shape.
 */
import { request } from './client';
import { personNameOrNull } from './personNames';
import {
  normalizeAllocationOrigin,
  normalizeContra,
  type AllocationContra,
  type AllocationOrigin,
} from './transactions';
import type {
  AccountLabel as WireAccountLabel,
  AllocationLine as WireAllocationLine,
  MovedFrom as WireMovedFrom,
  VirtualAccountRegisterLine as WireVirtualAccountRegisterLine,
  VirtualAccountRegisterPage as WireVirtualAccountRegisterPage,
  PageResponseEnvelopeRegisterLine as WireRegisterPage,
  EnvelopeRegisterLine as WireEnvelopeRegisterLine,
} from './generated';

/** The contract's `AllocationOrigin` enum (TMMAPI-0157), shared with the owner register. */
export type { AllocationOrigin };

/**
 * Where a re-split or assigned part's money came from (TMMAPI-0152 ask 2): the virtual account's
 * Unassigned, a live envelope, an envelope since removed, or several places at once.
 */
export type MovedFrom =
  | { kind: 'unassigned' }
  | { kind: 'envelope'; envelopeId: number | null; envelopeName: string }
  | { kind: 'removed' }
  | { kind: 'multiple' };
export type SourceAccountType = 'CASH' | 'REVOLVING' | 'LOAN';

export interface RegisterAccount {
  id: number;
  name: string;
  type: SourceAccountType;
  viewerOwns: boolean;
  last4: string | null;
  ownerDisplayName: string | null;
}

export interface EnvelopeRegisterLine {
  id: number;
  transactionId: number;
  postedOn: string;
  payee: string;
  account: RegisterAccount;
  amountMinor: number;
  runningBalanceMinor: number | null;
  categoryId: number | null;
  /** "Refund" / "Give-back" (MM-D-128), computed server-side; null otherwise. */
  contra: AllocationContra | null;
  origin: AllocationOrigin;
  meetingOccurrenceId: number | null;
  reversalOfAllocationId: number | null;
  reversedByAllocationId: number | null;
  /** The group's category; served on the published view too since TMMAPI-0143. */
  groupCategoryId: number | null;
  /** MM-D-65: the planned series, else the normalized payee, that groups this line. */
  groupKey: string | null;
  /**
   * The group's label (TMMAPI-0143): the series name for the owner, the payee for a sharer
   * (MM-D-51); null when not served.
   */
  groupLabel: string | null;
  groupSize: number | null;
  /** On a part a re-split or an assign posted (TMMAPI-0152); null on others and older parts. */
  movedFrom: MovedFrom | null;
  /** Who wrote the part: a person's public name or a split rule's name; null when not served. */
  actorName: string | null;
}

export interface EnvelopeRegisterQuery {
  from?: string | undefined;
  to?: string | undefined;
  categoryId?: number | undefined;
  cursor?: string | undefined;
  limit?: number | undefined;
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

/** One live part of a ledger row: an envelope's, or the virtual account's Unassigned. */
export interface VirtualAccountRegisterPart {
  id: number;
  /** True for the virtual account's own part (MM-D-112). */
  unassigned: boolean;
  envelopeId: number | null;
  envelopeName: string;
  amountMinor: number;
  categoryId: number | null;
  contra: AllocationContra | null;
}

export interface VirtualAccountRegisterLine {
  id: number;
  postedOn: string;
  payee: string;
  account: RegisterAccount;
  publishedMinor: number;
  /** The running balance after this row; null when not served. */
  runningBalanceMinor: number | null;
  allocations: VirtualAccountRegisterPart[];
  /** A contribution: the bank line's account owner isn't the virtual account's (TMMAPI-0152). */
  contributed: boolean;
  /** The contributing account owner, on a contribution only. Ids carry no authority. */
  contributorUserId: number | null;
}

export interface VirtualAccountRegisterTotals {
  /** Rows in this filter / window. */
  count: number;
  /** Rows ignoring the filters (the "n of N lines" denominator). */
  allCount: number;
  currency: string | null;
  inMinor: number;
  outMinor: number;
  netMinor: number;
}

export interface VirtualAccountRegisterPage extends Page<VirtualAccountRegisterLine> {
  totals: VirtualAccountRegisterTotals;
}

export interface VirtualAccountRegisterQuery {
  from?: string | undefined;
  to?: string | undefined;
  cursor?: string | undefined;
  limit?: number | undefined;
  /** Rows with a live part in this envelope. */
  envelopeId?: number | undefined;
  /** Rows with a live Unassigned part. */
  unassigned?: boolean | undefined;
  /** Rows with a part that has no category. */
  uncategorized?: boolean | undefined;
  /** Server-side text search (description, account, envelope or category). */
  q?: string | undefined;
}

export type UnassignedRegisterQuery = Pick<
  EnvelopeRegisterQuery,
  'from' | 'to' | 'cursor' | 'limit'
>;

const ACCOUNT_TYPES: ReadonlySet<string> = new Set(['CASH', 'REVOLVING', 'LOAN']);

function normalizeAccount(w: WireAccountLabel): RegisterAccount {
  return {
    id: w.id,
    name: w.name,
    // An unknown source type is still a source; `CASH` is the register's ordinary case.
    type: ACCOUNT_TYPES.has(w.type) ? w.type : 'CASH',
    viewerOwns: w.viewerOwns,
    last4: w.last4 ?? null,
    ownerDisplayName: personNameOrNull(w.ownerDisplayName),
  };
}

function normalizePart(w: WireAllocationLine): VirtualAccountRegisterPart {
  const unassigned = w.unassigned === true;
  return {
    id: w.id,
    unassigned,
    envelopeId: unassigned ? null : (w.envelopeId ?? null),
    envelopeName: w.envelopeName ?? '',
    amountMinor: w.amountMinor,
    categoryId: w.categoryId ?? null,
    contra: normalizeContra(w.contra),
  };
}

export function normalizeVirtualAccountRegisterLine(
  w: WireVirtualAccountRegisterLine,
): VirtualAccountRegisterLine {
  return {
    id: w.id,
    postedOn: w.postedOn,
    payee: w.payee,
    account: normalizeAccount(w.account),
    publishedMinor: w.publishedMinor,
    runningBalanceMinor: w.runningBalanceMinor ?? null,
    allocations: w.allocations.map(normalizePart),
    contributed: w.contributed,
    contributorUserId: w.contributorUserId ?? null,
  };
}

export function normalizeVirtualAccountRegisterPage(
  w: WireVirtualAccountRegisterPage,
): VirtualAccountRegisterPage {
  return {
    items: w.items.map(normalizeVirtualAccountRegisterLine),
    nextCursor: w.page.nextCursor,
    // Served by tmm-api 0145; never added up here.
    totals: { ...w.totals },
  };
}

function normalizeMovedFrom(w: WireMovedFrom | undefined): MovedFrom | null {
  if (w == null) return null;
  if (w.multiple === true) return { kind: 'multiple' };
  if (w.removed === true) return { kind: 'removed' };
  if (w.unassigned === true) return { kind: 'unassigned' };
  const name = w.envelopeName ?? null;
  if (name !== null && name.trim().length > 0) {
    return { kind: 'envelope', envelopeId: w.envelopeId ?? null, envelopeName: name };
  }
  return null;
}

/** A register line. */
export function normalizeRegisterLine(w: WireEnvelopeRegisterLine): EnvelopeRegisterLine {
  return {
    id: w.id,
    transactionId: w.transactionId,
    postedOn: w.postedOn,
    payee: w.payee,
    account: normalizeAccount(w.account),
    amountMinor: w.amountMinor,
    runningBalanceMinor: w.runningBalanceMinor ?? null,
    categoryId: w.categoryId ?? null,
    contra: normalizeContra(w.contra),
    origin: normalizeAllocationOrigin(w.origin) ?? 'USER',
    meetingOccurrenceId: w.meetingOccurrenceId ?? null,
    reversalOfAllocationId: w.reversalOfAllocationId ?? null,
    reversedByAllocationId: w.reversedByAllocationId ?? null,
    groupCategoryId: w.groupCategoryId ?? null,
    groupKey: w.groupKey ?? null,
    groupLabel: w.groupLabel ?? null,
    groupSize: w.groupSize ?? null,
    movedFrom: normalizeMovedFrom(w.movedFrom),
    actorName: personNameOrNull(w.actorName),
  };
}

/** `{ items, page: { nextCursor } }`. */
export function normalizeRegisterPage(w: WireRegisterPage): Page<EnvelopeRegisterLine> {
  return { items: w.items.map(normalizeRegisterLine), nextCursor: w.page.nextCursor };
}

export const registersApi = {
  /** GET /v1/virtual-accounts/{id}/register — the ledger; the filters are server-side and AND together. */
  async virtualAccountRegister(
    virtualAccountId: number,
    query: VirtualAccountRegisterQuery = {},
  ): Promise<VirtualAccountRegisterPage> {
    return normalizeVirtualAccountRegisterPage(
      await request<WireVirtualAccountRegisterPage>(
        'GET',
        `/v1/virtual-accounts/${virtualAccountId}/register`,
        {
          query: {
            from: query.from,
            to: query.to,
            cursor: query.cursor,
            limit: query.limit,
            envelopeId: query.envelopeId,
            unassigned: query.unassigned === true ? true : undefined,
            uncategorized: query.uncategorized === true ? true : undefined,
            q: query.q !== undefined && query.q.length > 0 ? query.q : undefined,
          },
        },
      ),
    );
  },
  /** GET /v1/virtual-accounts/{id}/unassigned/register — the Unassigned parts, as envelope register rows. */
  async unassignedRegister(
    virtualAccountId: number,
    query: UnassignedRegisterQuery = {},
  ): Promise<Page<EnvelopeRegisterLine>> {
    return normalizeRegisterPage(
      await request<WireRegisterPage>(
        'GET',
        `/v1/virtual-accounts/${virtualAccountId}/unassigned/register`,
        {
          query: { from: query.from, to: query.to, cursor: query.cursor, limit: query.limit },
        },
      ),
    );
  },
  /** GET /v1/envelopes/{envelopeId}/register — omitted `from` / `to` resolve to the envelope's period default (MM-D-40). */
  async envelopeRegister(
    envelopeId: number,
    query: EnvelopeRegisterQuery = {},
  ): Promise<Page<EnvelopeRegisterLine>> {
    return normalizeRegisterPage(
      await request<WireRegisterPage>('GET', `/v1/envelopes/${envelopeId}/register`, {
        query: {
          from: query.from,
          to: query.to,
          categoryId: query.categoryId,
          cursor: query.cursor,
          limit: query.limit,
        },
      }),
    );
  },
};
